import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { fetchBlogPosts } from "@/lib/api";
import { ArrowRight, Clock } from "lucide-react";

export default function Blog() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts()
      .then((data) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title={`Blog — Insights for UAE Facility Leaders | ${COMPANY.brand}`}
        description="Articles, guides and best practices on facility management, AMC, cleaning and HVAC for UAE operators."
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="container-orion">
          <div className="text-eyebrow">{t("blog.sectionLabel")}</div>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-slate-900 max-w-3xl">
            {t("blog.title")}
          </h1>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-white">
        <div className="container-orion">
          {loading ? (
            <div className="text-slate-500 text-sm">…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => {
                const title = lang === "ar" ? p.title_ar : p.title_en;
                const excerpt = lang === "ar" ? p.excerpt_ar : p.excerpt_en;
                return (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover"
                  >
                    <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                      <img
                        src={p.image_url?.startsWith("/blog/") ? `${process.env.REACT_APP_BACKEND_URL}/api${p.image_url}` : p.image_url}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-7">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] font-semibold text-blue-700">
                        <span>{p.category}</span>
                        <span className="text-slate-300">·</span>
                        <span className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-3 h-3" />
                          {p.read_time} {t("blog.readingTime")}
                        </span>
                      </div>
                      <h2 className="mt-4 font-heading text-xl font-medium text-slate-900 leading-snug group-hover:text-blue-700 transition-colors">
                        {title}
                      </h2>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {excerpt}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 group-hover:gap-3 transition-all">
                        {t("blog.readMore")}
                        <ArrowRight className="w-4 h-4 rtl-flip" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
